import * as Sentry from '@sentry/react';
import api from 'api';
import store from 'store';
import { produce } from 'immer';

import getProjectId from 'utils/url/getProjectId';
import getProjectState from 'store/getProjectState';
import setProjectState from 'store/setProjectState';

import { IProjectState } from 'store';
import IAsync from 'types/IAsync';
import IPaginatedResponse from 'types/IPaginatedResponse';
import IRunFile from 'types/IRunFile';

function generateId(idLength: number /* ← Max 9 */) {
  return Math.random()
    .toString(36)
    .slice(2, 2 + idLength);
}

function setRunFilesAsync(
  projectId: string,
  runId: string,
  runFilesAsync: IAsync<IPaginatedResponse<IRunFile>>,
) {
  setProjectState(
    projectId,
    produce((state: IProjectState) => {
      state.runFilesAsyncMap[runId] = runFilesAsync;
    }),
  );
}

export default function fetchRunFiles(
  runId: string,
  params?: { page?: number; pageSize?: number; search?: string },
): boolean {
  const projectId = getProjectId();
  if (!projectId) return false;

  const async = getProjectState(store.getState(), projectId).runFilesAsyncMap[
    runId
  ] || { status: 'INIT' };

  switch (async.status) {
    case 'PENDING':
    case 'REFRESH':
      // Already fetching
      return false;
    case 'INIT':
    case 'ERROR':
      setRunFilesAsync(projectId, runId, { status: 'PENDING' });
      break;
    case 'SUCCESS':
      setRunFilesAsync(projectId, runId, {
        status: 'REFRESH',
        data: async.data,
      });
      break;
  }

  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.pageSize) searchParams.set('page_size', String(params.pageSize));
  if (params?.search) searchParams.set('search', params.search);

  const qs = searchParams.toString();
  const url = `projects/${projectId}/workflows/${runId}/files${qs ? `?${qs}` : ''}`;

  api
    .get(url)
    .json()
    .then(
      (response) => {
        const paginated = response as IPaginatedResponse<
          Omit<IRunFile, 'id'>
        >;
        // Add client-side ids for table row keys
        const items: IRunFile[] = paginated.items.map((item) => ({
          ...item,
          id: generateId(5),
        }));

        setRunFilesAsync(projectId, runId, {
          status: 'SUCCESS',
          data: { ...paginated, items },
        });
      },
      (error) => {
        Sentry.captureException(error);
        setRunFilesAsync(projectId, runId, { status: 'ERROR' });
      },
    );

  return true;
}
